<template>
  <div class="description-form-new pb-5">
    <div class="row row-narrower align-items-start pb-5">
      <div class="col-md-6"
           v-if="dictionariesLoaded && rights.includes('planning')">
        <SelectGroup :options="plan_treatments"
                     variant="white"
                     formLabelClass="gray-label"
                     :label="$t('treatment_plan')"
                     :translate="false"
                     :showlabel="true"
                     :readonly="!canBeEdited"
                     :editMode="canBeEdited"
                     v-model="visit.visit_id"/>
      </div>
      <div class="col-md-6"
           v-if="assistantsLoaded">
        <SelectGroup :options="assistants"
                     title="full_name"
                     variant="white"
                     formLabelClass="gray-label"
                     :showErrors="true"
                     :readonly="assistantEditDisabled"
                     :editMode="canBeEdited"
                     validationId="assistant_id"
                     :label="$t('assistant')"
                     :showlabel="true"
                     v-model="visit.assistant_id"/>
      </div>
      <div class="col-md-6"
           v-if="rights.includes('amo_id')">
        <InputGroup v-model="visit.amoId"
                    :editMode="canBeEdited"
                    formLabelClass="gray-label"
                    :label="$t('amo_deal_id')"/>
      </div>
      <div class="col-md-6">
        <ServiceMultiSelect :label="$t('planned_services')"
                            :dblclick="true"
                            formLabelClass="gray-label"
                            :readonly="!canBeEdited"
                            :editMode="canBeEdited"
                            :resultsBelow="true"
                            v-model="visit.planned_services"/>
      </div>
    </div>
    <div class="row" v-if="showInsurancePolis">
      <div class="col-12">
        <label class="form-label gray-label">{{ $t('insurance_polis') }}</label>
      </div>
      <div class="col-9">
        <SelectGroup :options="polisesOptions"
                     v-if="polisesLoaded"
                     title="title"
                     variant="white"
                     :showErrors="true"
                     :readonly="!polisCanBeEdited"
                     validationId="insurance_polis_id"
                     :label="$t('insurance_polis')"
                     :showlabel="false"
                     v-model="visit.insurance_polis_id"/>
      </div>
      <div class="col-3">
        <a @click="addPolis"
           class="btn btn-themed btn-themed-outline btn-themed-squared w-100 add-polis-btn">
          <PlusSvg/>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue"
import {useStore} from "vuex"
import ServiceMultiSelect from "@/components/form/ServiceMultiSelect"
import SelectGroup from "@/components/form/SelectGroup"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { PatientService } from "@/services/api/patient.api.service"
import InputGroup from "@/components/form/InputGroup.vue"
import PlusSvg from '@/assets/svg-vue/patients/plus_tag.svg'

const props = defineProps({
  visit: {},
  counter: {}
})

const store = useStore()

const plan_visits = ref([])
const plan_treatments = ref([])
const assistants = ref([])
const polises = ref([])
const dictionariesLoaded = ref(false)
const assistantsLoaded = ref(false)
const polisesLoaded = ref(false)
const showInsurancePolis = ref(false)

const rights = computed(() => store.state.auth.rights)
const dataTableCounter = computed(() => store.state.datatable.dataTableCounter)

const patient_id = computed(() => props.counter ? props.counter : (props.visit.client_id ?? null))
const canBeEdited = computed(() => props.visit && Number(props.visit.is_paid) !== 1)
const assistantEditDisabled = computed(() => !props.visit || (props.visit.price > 0 && (props.visit.debt == 0 || (props.visit.payments && props.visit.payments.length))))
const polisCanBeEdited = computed(() => {
  if(!canBeEdited.value) return false
  return !(props.visit?.actual_services || []).length
})
const polisesOptions = computed(() => polises.value.filter(polis => !props.visit?.insurance_company_id || Number(polis.insurance_company_id) === Number(props.visit.insurance_company_id)))
const visitInsuranceCompanyId = computed(() => props.visit?.insurance_company_id)

const loadDictionaries = () => {
  dictionariesLoaded.value = false
  polisesLoaded.value = false
  if(!patient_id.value) return
  PatientService.getVisitDictionaries(patient_id.value, props.visit ? props.visit.visit_id : null).then(res => {
    plan_visits.value = res.data.plan_visits
    plan_treatments.value = res.data.plan_treatments
    store.commit('setPolises', res.data.polises)
    polises.value = res.data.polises
    dictionariesLoaded.value = true
    polisesLoaded.value = true
  })
}

const loadAssistants = () => {
  EloquentService.dataTable('assistant', 1, { pagination: 999 }).then(res => {
    assistants.value = res.data.result.data
    assistantsLoaded.value = true
  })
}
const addPolis = () => {
  store.commit('setViewModel', 'insurance_polis')
  store.commit('setModule', 'insurance')
  store.commit('setSelectedModel', {
    patient: props.visit?.patient ?? null,
    patient_id: patient_id.value ?? null
  })
  store.commit('setShowModal', true)
}

onMounted(() => {
  loadAssistants()
})

watch(patient_id, (val) => {
  if(val) {
    loadDictionaries()
  }
}, { immediate: true })
watch(() => props.counter, () => {
  loadDictionaries()
})
watch(dataTableCounter, () => {
  loadDictionaries()
})
watch(visitInsuranceCompanyId, () => {
  props.visit.insurance_polis_id = null
})
</script>

<style scoped lang="scss">
.add-polis-btn {
  height: 44px;
}
.description-form-new {
  min-height: 50vh;
}
</style>
